class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.updateDeliveryEstimate();
    this.checkGiftThreshold();

    // Add cart update subscriber
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.checkGiftThreshold();
      this.updateDeliveryEstimate();
    });
  }

  // Add cleanup
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        section.selector && !section.selector.includes('cart-icon-bubble')
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.updateDeliveryEstimate();

    setTimeout(async () => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      
      // Only show popup if this is a regular product add (not the gift)
      if (parsedState && parsedState.id !== 49936277569862) {
        const popup = document.querySelector('cart-confirmation-popup');
        if (popup instanceof HTMLElement && typeof popup.show === 'function') {
          // Get the variant data which contains compareAtPrice
          try {
            const response = await fetch(`/products/${parsedState.handle}.js`);
            const productData = await response.json();
            const variant = productData.variants.find(v => v.id === parsedState.variant_id);
            
            const popupData = {
              title: parsedState.title || parsedState.product_title,
              price: `£${((parsedState.discounted_price || parsedState.price || 0)/100).toFixed(2)}`,
              featured_image: {
                url: parsedState.featured_image?.url || parsedState.image
              }
            };
            
            // Add compare at price if available from variant
            if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
              popupData.compare_at_price = `£${(variant.compare_at_price/100).toFixed(2)}`;
            }
            
            popup.show(popupData);
          } catch (error) {
            // Fallback to showing popup without compare price
            popup.show({
              title: parsedState.title || parsedState.product_title,
              price: `£${((parsedState.discounted_price || parsedState.price || 0)/100).toFixed(2)}`,
              featured_image: {
                url: parsedState.featured_image?.url || parsedState.image
              }
            });
          }
        }
      }
    });

    this.checkGiftThreshold();
  }

  isGiftItem(parsedState) {
    // Check if this is the gift item being added
    if (parsedState && parsedState.items && parsedState.items.length > 0) {
      const lastItem = parsedState.items[parsedState.items.length - 1];
      return lastItem.properties && (
        lastItem.properties['_gift_item'] === true || 
        lastItem.properties['Gift'] === 'Free Gift with Purchase'
      );
    }
    return false;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateDeliveryEstimate() {
    const deliveryElement = this.querySelector('.delivery-estimate');
    if (!deliveryElement) return;
    
    const minDays = parseInt(deliveryElement.dataset.minDays) || 2;
    const maxDays = parseInt(deliveryElement.dataset.maxDays) || 5;
    
    const start = new Date();
    start.setDate(start.getDate() + minDays);
    const end = new Date();
    end.setDate(end.getDate() + maxDays);

    const formatDate = (date, includeMonth = false) => {
        const day = date.getDate();
        const suffix = getOrdinalSuffix(day);
        return includeMonth 
            ? `${date.toLocaleString('default', { month: 'long' })} ${day}${suffix}`
            : `${day}${suffix}`;
    };

    const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    deliveryElement.textContent = `Free delivery on ${formatDate(start, true)} - ${formatDate(end)}`;
  }

  async checkGiftThreshold() {
    const progressBar = document.querySelector('.cart-progress');
    if (!progressBar) return false;
    
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Filter out gift item and count remaining items
      const cartCount = cart.items.reduce((count, item) => {
        return item.variant_id === 49936277569862 ? count : count + item.quantity;
      }, 0);
      
      // Update progress bar data attribute with filtered count
      progressBar.dataset.cartCount = cartCount;
      
      const meetsThreshold = cartCount >= 3;
      const giftInCart = cart.items.some(item => item.variant_id === 49936277569862);
      
      if (meetsThreshold && !giftInCart) {
        this.addGiftToCart();
      } else if (!meetsThreshold && giftInCart) {
        this.removeGiftFromCart();
      }
      
      return meetsThreshold;
    } catch (error) {
      return false;
    }
  }

  async addGiftToCart() {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: 49936277569862,
            quantity: 1,
            properties: {
              'Gift': 'Free Gift with Purchase',
              '_gift_item': true,
              '_original_price': 0,
              '_compare_at_price': 0
            }
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add to cart failed');
      }
      
      const result = await response.json();
      
      // Get fresh cart HTML
      const cartResponse = await fetch('/?section_id=cart-drawer');
      const cartHTML = await cartResponse.text();
      
      // Create the parsed state the drawer expects
      const parsedState = {
        id: 49936277569862,
        sections: {
          'cart-drawer': cartHTML,
          'cart-icon-bubble': await (await fetch('/?section_id=cart-icon-bubble')).text()
        }
      };

      // Use the drawer's own render method
      this.renderContents(parsedState);
      
    } catch (error) {
    }
  }

  async removeGiftFromCart() {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: {
            '49936277569862': 0
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Remove from cart failed');
      }
      
      const result = await response.json();
      
      // Get fresh cart HTML
      const cartResponse = await fetch('/?section_id=cart-drawer');
      const cartHTML = await cartResponse.text();
      
      // Create the parsed state the drawer expects
      const parsedState = {
        id: 49936277569862,
        sections: {
          'cart-drawer': cartHTML,
          'cart-icon-bubble': await (await fetch('/?section_id=cart-icon-bubble')).text()
        }
      };

      // Use the drawer's own render method
      this.renderContents(parsedState);
      
    } catch (error) {
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.onQuantityChange.bind(this));
  }

  onQuantityChange(event) {
    if (!event.target.matches('.quantity__input')) return;
    
    const quantity = parseInt(event.target.value);
    const itemId = event.target.dataset.index;
    const item = document.getElementById(`CartDrawer-Item-${itemId}`);
    
    if (item) {
      // Skip price updates for gift items
      if (item.hasAttribute('data-gift-item')) return;
      
      const oldPrice = item.querySelector('.cart-item__old-price');
      const finalPrice = item.querySelector('.price--end');
      
      if (oldPrice) {
        const baseOldPrice = oldPrice.dataset.basePrice;
        oldPrice.innerText = `£${(baseOldPrice * quantity).toFixed(2)}`;
      }
      
      if (finalPrice) {
        const baseFinalPrice = finalPrice.dataset.basePrice;
        finalPrice.innerText = `£${(baseFinalPrice * quantity).toFixed(2)}`;
      }
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
